import { Card, Icon, ToggleButton, ToggleButtonGroup, Pagination } from "@mui/material";
import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";
import Alert from "custom/Alert";
import DashboardLayout from "custom/LayoutContainers/DashboardLayout";
import DashboardNavbar from "custom/Navbars/DashboardNavbar";
import { useState, useEffect } from "react";
import FirstPageViewer from "components/PdfViewer";
import Swal from "sweetalert2";
import {
  getSoundMaterialItems,
  deleteSoundMaterialItem as deleteSoundMaterialItemAction,
} from "redux/actions/pages/sound-material";
import { connect } from "react-redux";
import PropTypes from "prop-types";

function SoundItems({ soundItems, startScreen, deleteSoundMaterialItem }) {
  const [soundMaterials, setSoundMaterials] = useState([]);
  const [filteredMaterials, setFilteredMaterials] = useState([]);
  const [selectedLetter, setSelectedLetter] = useState("ALL");
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [availableLetters, setAvailableLetters] = useState(["ALL"]);

  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const itemsPerPage = 18;
  useEffect(() => {
    startScreen();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (typeof soundItems !== "undefined" && soundItems) {
      // Veriyi diziye dönüştür
      const soundItemArray = Object.values(soundItems);
      // Sadece ses materyallerini filtrele (lookup verilerini hariç tut)
      const soundMaterialsOnly = soundItemArray.filter(
        (item) => item && item.id && item.letter && item.name && item.file && !item.lookupKey // lookupKey özelliği olmayan öğeleri al
      );

      // Backend'den gelen veriyi bileşenin beklediği formata dönüştür
      const formattedMaterials = soundMaterialsOnly.map((item) => ({
        id: item.id,
        letter: item.letter,
        file: item.file,
        name: item.name,
        type: item.type,
        isActive: item.is_active,
        soundOrder: item.sound_order,
      }));

      // Eğer boş dizi gelirse, yerel state'i temizle
      if (formattedMaterials.length === 0) {
        setSoundMaterials([]);
        setFilteredMaterials([]);
        setAvailableLetters(["ALL"]);
      } else {
        setSoundMaterials(formattedMaterials);
        setFilteredMaterials(formattedMaterials);
        setTotalItems(formattedMaterials.length);

        // Harfleri çıkar
        const letters = formattedMaterials.map((material) => material.letter);
        setAvailableLetters(["ALL", ...new Set(letters)]);
      }

      setIsLoading(false);
    }
  }, [soundItems]);

  // Function to fetch sound materials by letter
  const fetchSoundMaterialsByLetter = async (letter) => {
    if (letter === "ALL") {
      // If "ALL" is selected, show all materials
      setFilteredMaterials(soundMaterials);
      setTotalItems(soundMaterials.length);
      setCurrentPage(1);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Filter the existing materials
      const filtered = soundMaterials.filter((material) => material.letter === letter);
      setFilteredMaterials(filtered);
      setTotalItems(filtered.length);
      setCurrentPage(1);
    } catch (err) {
      setError(`${letter} harfine ait ses materyalleri yüklenirken bir hata oluştu.`);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle letter selection change
  const handleLetterChange = (event, newLetter) => {
    if (newLetter !== null) {
      setSelectedLetter(newLetter);
      fetchSoundMaterialsByLetter(newLetter);
    }
  };

  const handlePageChange = (event, page) => {
    setCurrentPage(page);
  };

  const getCurrentPageMaterials = () => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredMaterials.slice(startIndex, endIndex);
  };

  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const currentPageMaterials = getCurrentPageMaterials();

  // Handle material deletion
  const handleDelete = (materialId) => {
    Swal.fire({
      title: "Emin misiniz?",
      text: "Bu ses materyalini silmek istediğinize emin misiniz?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Evet, sil!",
      cancelButtonText: "İptal",
    }).then((result) => {
      if (result.isConfirmed) {
        // Redux action'ı çağır
        deleteSoundMaterialItem(materialId);

        // Yerel state'i hemen güncelle
        const updatedMaterials = soundMaterials.filter((material) => material.id !== materialId);
        setSoundMaterials(updatedMaterials);

        // Filtrelenmiş materyalleri de güncelle
        let updatedFilteredMaterials;
        if (selectedLetter === "ALL") {
          updatedFilteredMaterials = updatedMaterials;
          setFilteredMaterials(updatedFilteredMaterials);
        } else {
          updatedFilteredMaterials = updatedMaterials.filter(
            (material) => material.letter === selectedLetter
          );
          setFilteredMaterials(updatedFilteredMaterials);
        }

        setTotalItems(updatedFilteredMaterials.length);

        const newTotalPages = Math.ceil(updatedFilteredMaterials.length / itemsPerPage);
        if (currentPage > newTotalPages && newTotalPages > 0) {
          setCurrentPage(newTotalPages);
        }

        // Harfleri güncelle
        const letters = updatedMaterials.map((material) => material.letter);
        setAvailableLetters(["ALL", ...new Set(letters)]);

        setSelectedLetter("ALL");
        setCurrentPage(1);
        // Başarı bildirimi göster
        Swal.fire("Silindi!", "Ses materyali başarıyla silindi.", "success");

        // Doğrudan startScreen fonksiyonunu çağır
        setTimeout(() => {
          startScreen();
        }, 1000);
      }
    });
  };

  const renderContent = () => {
    if (isLoading) {
      return <SuiTypography>Ses materyalleri yükleniyor...</SuiTypography>;
    }

    if (error) {
      return <SuiTypography color="error">{error}</SuiTypography>;
    }

    if (soundMaterials.length === 0) {
      return <SuiTypography>Henüz ses materyali bulunmamaktadır.</SuiTypography>;
    }

    return (
      <>
        {/* Letter filter buttons */}
        <SuiBox mb={3}>
          <ToggleButtonGroup
            value={selectedLetter}
            exclusive
            onChange={handleLetterChange}
            aria-label="letter filter"
            size="small"
          >
            {availableLetters.map((letter) => (
              <ToggleButton
                key={letter}
                value={letter}
                aria-label={letter === "ALL" ? "Tümü" : letter[0]}
              >
                {letter === "ALL" ? "Tümü" : letter[0]}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </SuiBox>

        {/* Materials display */}
        {filteredMaterials.length === 0 ? (
          <SuiTypography>Seçilen harf için ses materyali bulunmamaktadır.</SuiTypography>
        ) : (
          <>
            <SuiBox
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 3,
                justifyContent: "flex-start",
                minHeight: "400px",
              }}
            >
              {currentPageMaterials.map((material) => (
                <SuiBox
                  key={`material-${material.id}`}
                  sx={{
                    width: "198px",
                    height: "280px",
                    backgroundColor: "white",
                    borderRadius: "lg",
                    boxShadow: "sm",
                    border: "1px solid",
                    borderColor: "grey.300",
                    flexShrink: 0,
                    position: "relative",
                    display: "flex",
                    flexDirection: "column",
                    padding: 2,
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <SuiBox
                    sx={{
                      position: "absolute",
                      top: "10px",
                      left: "10px",
                      width: "30px",
                      height: "30px",
                      borderRadius: "50%",
                      backgroundColor: "rgb(52, 71, 103)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      zIndex: 3,
                    }}
                  >
                    <SuiTypography
                      variant="button"
                      sx={{
                        color: "rgb(255, 255, 255)",
                        fontWeight: 600,
                      }}
                    >
                      {(currentPage - 1) * itemsPerPage +
                        currentPageMaterials.indexOf(material) +
                        1}
                    </SuiTypography>
                  </SuiBox>

                  <SuiBox
                    onClick={() => handleDelete(material.id)}
                    sx={{
                      position: "absolute",
                      top: "10px",
                      right: "10px",
                      width: "30px",
                      height: "30px",
                      borderRadius: "50%",
                      backgroundColor: "rgb(220, 53, 69)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      zIndex: 3,
                      cursor: "pointer",
                      "&:hover": {
                        backgroundColor: "rgb(200, 35, 51)",
                      },
                    }}
                  >
                    <Icon sx={{ color: "rgb(255, 255, 255)", fontSize: "1rem" }}>close</Icon>
                  </SuiBox>

                  <FirstPageViewer fileUrl={material.file} />
                  <SuiTypography variant="caption" mt={1}>
                    {material.name}
                  </SuiTypography>
                </SuiBox>
              ))}
            </SuiBox>

            {/* Pagination */}
            {totalPages > 1 && (
              <SuiBox
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  mt: 4,
                  mb: 2,
                }}
              >
                <Pagination
                  count={totalPages}
                  page={currentPage}
                  onChange={handlePageChange}
                  color="primary"
                  size="large"
                  showFirstButton
                  showLastButton
                />
              </SuiBox>
            )}

            <SuiBox sx={{ textAlign: "center", mt: 2 }}>
              <SuiTypography variant="caption" color="text">
                {`${(currentPage - 1) * itemsPerPage + 1}-${Math.min(
                  currentPage * itemsPerPage,
                  totalItems
                )} / ${totalItems} ses materyali gösteriliyor`}
              </SuiTypography>
            </SuiBox>
          </>
        )}
      </>
    );
  };

  return (
    <>
      <Alert />
      <DashboardLayout>
        <DashboardNavbar />
        <SuiBox mt={3} mb={4}>
          <Card>
            <SuiBox p={3}>
              <SuiBox mb={4}>
                <SuiTypography variant="h5" fontWeight="medium" mb={2}>
                  Ses Materyalleri
                </SuiTypography>
                {renderContent()}
              </SuiBox>
            </SuiBox>
          </Card>
        </SuiBox>
      </DashboardLayout>
    </>
  );
}

SoundItems.propTypes = {
  soundItems: PropTypes.objectOf(
    PropTypes.shape({
      id: PropTypes.number,
      letter: PropTypes.string,
      name: PropTypes.string,
      type: PropTypes.string,
      file: PropTypes.string,
      is_active: PropTypes.bool,
      sound_order: PropTypes.number,
    })
  ),
  startScreen: PropTypes.func.isRequired,
  deleteSoundMaterialItem: PropTypes.func.isRequired,
};

SoundItems.defaultProps = {
  soundItems: {},
};

const mapStateToProps = (state) => ({
  loading: state.ui.loading,
  soundItems: state.data,
});

const mapDispatchToProps = {
  startScreen: getSoundMaterialItems,
  deleteSoundMaterialItem: deleteSoundMaterialItemAction,
};
export default connect(mapStateToProps, mapDispatchToProps)(SoundItems);
